import React from 'react';
import { camelizeKeys } from 'humps';
import { Helmet } from '../../components/Helmet';
import { FacebookSDKScript } from '../../components/FacebookSDKScript';
import { FacebookConnectScript } from '../../components/FacebookConnectScript';
import { WellnessArticlePageContainer } from '../../containers/WellnessArticlePageContainer';
import { WellnessArticlePageJSONLD, WellnessArticlePageJSONLDProps } from '../../containers/WellnessArticlePageContainer/WellnessArticlePageJSONLD';
import { WagContentApiEndpoint, WAG_CONTENT_API_URL } from '../../constants/endpoints';
import { DEFAULT_CACHE_POLICY } from '../../constants/cache';
import { HttpHeaderKey } from '../../constants/http-header-key';
import { FACEBOOK_APP_ID } from '../../constants/app';
import { requestHandler } from '../../utils/requestHandler';
import { CamelizedWellnessArticleResponse } from '../../types/api';
import { useCanonicalUrl } from '../../hooks/useCanonicalUrl';
import { PreloadImage } from '../../components/PreloadImage';
import { applyPathParams } from '../../wag-react/common/api/utils/applyPathParams';
import { httpGet } from '../../wag-react/common/api/api';
import { Layout } from '../../components/Layout';
type WellnessArticleProps = CamelizedWellnessArticleResponse & {
  slug: string;
};
const WellnessArticle = (props: WellnessArticleProps) => {
  const {
    metaTitle,
    metaDesc,
    structuredData,
    heroImageUrl
  } = props;
  const {
    canonicalUrl
  } = useCanonicalUrl();
  return <>
      <Helmet canonical={canonicalUrl} title={metaTitle} description={metaDesc} robots="index, follow" data-sentry-element="Helmet" data-sentry-source-file="[slug].tsx" />
      <PreloadImage src={heroImageUrl} data-sentry-element="PreloadImage" data-sentry-source-file="[slug].tsx" />
      <Layout data-sentry-element="Layout" data-sentry-source-file="[slug].tsx">
        <WellnessArticlePageContainer {...props} data-sentry-element="WellnessArticlePageContainer" data-sentry-source-file="[slug].tsx" />
      </Layout>
      <FacebookSDKScript facebookAppId={FACEBOOK_APP_ID} data-sentry-element="FacebookSDKScript" data-sentry-source-file="[slug].tsx" />
      <FacebookConnectScript data-sentry-element="FacebookConnectScript" data-sentry-source-file="[slug].tsx" />
      {structuredData && <WellnessArticlePageJSONLD structuredData={structuredData as WellnessArticlePageJSONLDProps['structuredData']} />}
    </>;
};
const handleGetServerSideProps = requestHandler({
  headers: {
    [HttpHeaderKey.CacheControl]: DEFAULT_CACHE_POLICY
  }
});
export const getServerSideProps = handleGetServerSideProps(async ({
  query
}) => {
  const {
    slug
  } = query;
  const endpoint = applyPathParams(WagContentApiEndpoint.WellnessArticle)({
    slug: encodeURI(slug as string)
  });
  const {
    data
  } = await httpGet(endpoint, {
    baseURL: WAG_CONTENT_API_URL,
    // To support 301/302 http statuses, do not follow redirects from responses
    maxRedirects: 0
  });
  const article = camelizeKeys(data as Record<string, unknown>) as CamelizedWellnessArticleResponse;
  return {
    props: {
      ...article,
      slug
    }
  };
});
export default WellnessArticle;